import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { freetextApi } from '@/services/api/freetext'
import { ResolveFreetextInput } from '@/types/admin'

/**
 * Hook to fetch freetext suggestions
 * @param resolved - Filter by resolution status (true = resolved, false = unresolved, undefined = all)
 */
export const useFreetextSuggestions = (resolved?: boolean) => {
  return useQuery({
    queryKey: ['freetext-suggestions', resolved],
    queryFn: () => freetextApi.list(resolved),
    staleTime: 30000, // 30 seconds
  })
}

/**
 * Hook to resolve a freetext suggestion
 */
export const useResolveFreetext = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ suggestionId, input }: { suggestionId: string; input: ResolveFreetextInput }) =>
      freetextApi.resolve(suggestionId, input),
    onSuccess: () => {
      // Invalidate both unresolved and all queries to refresh the lists
      queryClient.invalidateQueries({ queryKey: ['freetext-suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
  })
}

/**
 * Hook to de-duplicate freetext suggestions by updating duplicate with master's Wikidata IDs
 */
export const useMergeFreetext = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      masterId,
      duplicateId,
      mergeComposer = true,
      mergeWork = true,
    }: {
      masterId: string
      duplicateId: string
      mergeComposer?: boolean
      mergeWork?: boolean
    }) => freetextApi.merge(masterId, duplicateId, mergeComposer, mergeWork),
    onSuccess: () => {
      // Invalidate freetext and suggestions queries to refresh the lists
      queryClient.invalidateQueries({ queryKey: ['freetext-suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
  })
}
